import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp, collection } from 'firebase/firestore';
import { auth, db, functions as fbFunctions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const initializeUserDocument = async (user) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        console.log('Creating new user document for:', user.email);
        const newUserData = {
          email: user.email,
          credits: 0,
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
        };

        await setDoc(userRef, newUserData);
        console.log('User document created successfully');
        setUserData(newUserData);
      } else {
        // Update last login time
        await setDoc(userRef, { lastLogin: Timestamp.now() }, { merge: true });
        setUserData(userDoc.data());
      }
    } catch (err) {
      console.error('Error initializing user document:', err);
      throw err; // Re-throw to be caught by the parent try-catch
    }
  };

  const maybeGrantVerificationBonus = async (user) => {
    try {
      if (!user?.emailVerified) return;
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      const granted = snap.exists() && snap.data()?.verificationBonusGranted === true;
      if (granted) return;
      const award = httpsCallable(fbFunctions, 'awardVerificationCredits');
      await award();
    } catch (e) {
      console.error('Failed to award verification credits automatically', e);
    }
  };

  const maybeGrantGoogleVerificationBonus = async (user) => {
    try {
      // Google users are always verified; award them the same 300 credits
      if (!user || user.providerData.length === 0) return;
      const hasGoogle = user.providerData.some(p => p.providerId === 'google.com');
      if (!hasGoogle) return;
      
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      const granted = snap.exists() && snap.data()?.verificationBonusGranted === true;
      if (granted) return;
      
      const award = httpsCallable(fbFunctions, 'awardVerificationCredits');
      await award();
    } catch (e) {
      console.error('Failed to award Google verification credits automatically', e);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        try {
          if (user) {
            console.log('User authenticated:', user.email);
            await initializeUserDocument(user);
            setUser(user);
            maybeGrantVerificationBonus(user);
            maybeGrantGoogleVerificationBonus(user);
          } else {
            console.log('No user authenticated');
            setUser(null);
          }
        } catch (err) {
          console.error('Auth state change error:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Auth observer error:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, userData, loading, error }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
